// @ts-nocheck
import React, { useState, useEffect, useRef } from 'react';
import { ChevronDown, ChevronUp, Settings, Sparkles } from 'lucide-react';
import axios from 'axios';
import { supabase } from '../../utils/supabaseClient';
import './ConfigCategoryPreview.css';

const SECTION_COLORS = {
  revenue: '#16a34a',
  cogs: '#dc2626',
  opex: '#2563eb',
  other_income: '#059669',
  other_expenses: '#9333ea',
  amortization: '#6b7280',
  depreciation: '#6b7280',
  interest: '#6b7280',
  tax: '#6b7280',
};

const NAME_COLOR_PATTERNS = [
  { pattern: /revenue|sales/i, color: '#16a34a' },
  { pattern: /cost.*(?:sales|goods)|cogs/i, color: '#dc2626' },
  { pattern: /operat.*expense|opex/i, color: '#2563eb' },
  { pattern: /other.*income/i, color: '#059669' },
  { pattern: /other.*expense/i, color: '#9333ea' },
];

const BELOW_EBITDA_KEYS = new Set(['amortization', 'depreciation', 'interest', 'tax']);
const BELOW_EBITDA_PATTERNS = [/amortiz/i, /depreci/i, /interest/i, /\btax\b/i];

function isBelowEbitda(key, name) {
  if (BELOW_EBITDA_KEYS.has(key)) return true;
  return BELOW_EBITDA_PATTERNS.some(p => p.test(name));
}

function getSectionColor(key, name) {
  if (SECTION_COLORS[key]) return SECTION_COLORS[key];
  for (const { pattern, color } of NAME_COLOR_PATTERNS) {
    if (pattern.test(name)) return color;
  }
  return '#6b7280';
}

function buildDisplayHierarchy(templateStructure) {
  if (!templateStructure || typeof templateStructure !== 'object') return [];
  return Object.entries(templateStructure).map(([key, section]) => ({
    key,
    name: section.name || key,
    children: (section.subcategories || []).map(sub => ({ name: sub.name })),
  }));
}

function buildFromCategoryHierarchy(categoryHierarchy) {
  if (!Array.isArray(categoryHierarchy)) return [];

  const sectionMap = {};
  const order = [];

  categoryHierarchy.forEach(cat => {
    if (cat.parent) {
      if (!sectionMap[cat.parent]) {
        sectionMap[cat.parent] = { name: cat.parent, children: [] };
        order.push(cat.parent);
      }
      sectionMap[cat.parent].children.push({ name: cat.name });
    } else if (cat.depth === 0) {
      if (!sectionMap[cat.name]) {
        sectionMap[cat.name] = { name: cat.name, children: [] };
        order.push(cat.name);
      }
    }
  });

  return order.map(name => ({
    key: name.toLowerCase().replace(/[^a-z0-9]/g, '_'),
    ...sectionMap[name],
  }));
}

const ConfigCategoryPreview = ({ config, isDefault, isOpen, onToggle, configType, mode = 'inline' }) => {
  const [defaultConfig, setDefaultConfig] = useState(null);
  const [loadingDefault, setLoadingDefault] = useState(false);
  const fetchedRef = useRef(false);

  const isPanelMode = mode === 'panel';
  const shouldShow = isPanelMode || isOpen;

  useEffect(() => {
    if (isDefault && shouldShow && !fetchedRef.current) {
      fetchedRef.current = true;
      setLoadingDefault(true);

      const fetchDefault = async () => {
        try {
          await supabase.auth.getSession();
          const response = await axios.get(
            `${import.meta.env.VITE_BACKEND_BASE_URL}/tenant-config/default/${configType || 'income_statement'}`,
            {
              withCredentials: true,
              headers: { 'x-api-key': import.meta.env.VITE_API_KEY },
            }
          );
          setDefaultConfig(response.data);
        } catch (err) {
          // silently fail — preview just won't show
        } finally {
          setLoadingDefault(false);
        }
      };
      fetchDefault();
    }
  }, [isDefault, shouldShow, configType]);

  useEffect(() => {
    if (!isDefault) {
      fetchedRef.current = false;
      setDefaultConfig(null);
    }
  }, [isDefault]);

  const activeConfig = isDefault ? defaultConfig : config;
  const templateStructure = activeConfig?.template_structure;
  const frontendConfig = activeConfig?.frontend_config;
  const categories = activeConfig?.categories || [];

  let sections = [];
  if (templateStructure && Object.keys(templateStructure).length > 0) {
    sections = buildDisplayHierarchy(templateStructure);
  } else if (frontendConfig?.categoryHierarchy?.length > 0) {
    sections = buildFromCategoryHierarchy(frontendConfig.categoryHierarchy);
  }

  const mainSections = sections.filter(s => !isBelowEbitda(s.key, s.name));
  const belowEbitdaSections = sections.filter(s => isBelowEbitda(s.key, s.name));

  const hasHierarchy = sections.length > 0;
  const configName = isDefault
    ? 'Default P&L'
    : (config?.config_name || config?.tenant_name || 'Configuration');

  const hasGuidelines = !isDefault && (
    config?.llm_guidelines_available ||
    (config?.llm_guidelines && config.llm_guidelines.length > 100)
  );

  const totalCategories = isDefault
    ? (defaultConfig?.categories?.length || 0)
    : (config?.categories?.length || 0);

  const renderSections = (sectionList) =>
    sectionList.map((section) => {
      const color = getSectionColor(section.key, section.name);
      return (
        <div key={section.key} className="ccp-section">
          <div className="ccp-section-header" style={{ borderLeftColor: color }}>
            <span className="ccp-section-name">{section.name}</span>
            {section.children.length > 0 && (
              <span className="ccp-section-count">{section.children.length}</span>
            )}
          </div>
          {section.children.length > 0 && (
            <div className="ccp-section-children">
              {section.children.map((child, i) => (
                <div key={i} className="ccp-child">
                  <span className="ccp-child-dot">&#183;</span>
                  <span className="ccp-child-name">{child.name}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      );
    });

  const renderContent = () => (
    <>
      <div className="ccp-header">
        <h5 className="ccp-title">
          <Settings size={14} />
          {configName} Categories
        </h5>
        {hasGuidelines && (
          <div className="ccp-guidelines-badge">
            <Sparkles size={14} />
            <span>Custom AI mapping guidelines active</span>
          </div>
        )}
      </div>

      {isDefault && loadingDefault ? (
        <div className="ccp-loading">
          <div className="ccp-spinner"></div>
          <span>Loading default categories...</span>
        </div>
      ) : hasHierarchy ? (
        <div className="ccp-sections">
          {renderSections(mainSections)}
          {belowEbitdaSections.length > 0 && (
            <>
              <div className="ccp-below-ebitda-divider">
                <span>Below EBITDA</span>
              </div>
              {belowEbitdaSections.map((section) => (
                <div key={section.key} className="ccp-below-ebitda-item">
                  <span className="ccp-below-ebitda-dot"></span>
                  <span className="ccp-below-ebitda-name">{section.name}</span>
                </div>
              ))}
            </>
          )}
        </div>
      ) : categories.length > 0 ? (
        <div className="ccp-flat-list">
          {categories.map((cat, i) => (
            <div key={i} className="ccp-flat-item">{cat}</div>
          ))}
        </div>
      ) : (
        <p className="ccp-empty">No categories defined in this configuration.</p>
      )}
    </>
  );

  if (isPanelMode) {
    return (
      <div className="ccp-wrapper">
        <div className="ccp-card">
          {renderContent()}
        </div>
      </div>
    );
  }

  return (
    <div className="ccp-wrapper">
      <button type="button" className="ccp-toggle" onClick={onToggle}>
        <span className="ccp-toggle-label">
          Preview Categories
          {totalCategories > 0 && (
            <span className="ccp-toggle-count">{totalCategories}</span>
          )}
        </span>
        {isOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
      </button>

      {isOpen && (
        <div className="ccp-panel">
          {renderContent()}
        </div>
      )}
    </div>
  );
};

export default ConfigCategoryPreview;
